import ReactDOM from 'react-dom/client';
import './zodErrorMap';
import './styles/index.css';

import { AuthProvider } from './contexts/AuthProvider';
import AppRouter from './AppRouter';
import { AwsRum } from 'aws-rum-web';

import { QueryProvider } from './contexts/QueryProvider';
import { ConfigProvider } from './contexts/ConfigProvider';
import { useConfig } from './contexts/ConfigContext';
import { ActiveTenantProvider } from './contexts/ActiveTenantProvider';
import { PermissionsProvider } from './contexts/PermissionsProvider';

export const AppInitializer: React.FC = () => {
  const { config } = useConfig();

  // Configure AWS RUM if in test or prod
  if (config.app.stage === 'test' || config.app.stage === 'prod') {
    const RUM_ENDPOINT = `https://dataplane.rum.${config.aws.region}.amazonaws.com`;

    const getInteractionId = (event: Event): string => {
      const eventPath = event.composedPath() as Element[];
      for (const element of eventPath) {
        if (element.hasAttribute && element.hasAttribute('data-rum-id')) {
          return element.getAttribute('data-rum-id') as string;
        }
      }
      return '';
    };

    try {
      const rumConfig = {
        sessionSampleRate: 1,
        guestRoleArn: config.rum.guestRoleArn,
        identityPoolId: config.rum.identityPoolId,
        endpoint: RUM_ENDPOINT,
        telemetries: [
          'errors',
          'performance',
          'http',
          [
            'interaction',
            {
              events: [{ event: 'click', element: document }],
              interactionId: getInteractionId
            }
          ]
        ],
        allowCookies: true,
        enableXRay: false
      };
      new AwsRum(config.rum.appId, config.rum.appVersion, config.aws.region, rumConfig);
    } catch (error) {
      console.error('Error initializing AWS RUM', error);
      // Ignore errors thrown during CloudWatch RUM web client initialization
    }
  }

  return (
    <QueryProvider baseUrl={config.api.apiUrl}>
      <AuthProvider>
        <ActiveTenantProvider>
          <PermissionsProvider>
            <AppRouter />
          </PermissionsProvider>
        </ActiveTenantProvider>
      </AuthProvider>
    </QueryProvider>
  );
};

// Initialize dark mode before React renders to avoid flash on all pages (including login)
const storedDarkMode = localStorage.getItem('darkMode');
const isDark =
  storedDarkMode !== null ? storedDarkMode === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches;
document.documentElement.classList.toggle('dark', isDark);

if (global === undefined) {
  global = window;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ConfigProvider>
    <AppInitializer />
  </ConfigProvider>
);
